import type { AdminObservabilityAggregateSummary } from "../../contracts/admin-read-models";
import type { AdminDashboardLocale } from "./admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "./admin-dashboard-copy";

export type AdminObservabilityAggregateField = keyof AdminObservabilityAggregateSummary;

export type AdminObservabilityAggregateItemViewModel = {
  id: AdminObservabilityAggregateField;
  label: string;
  value: string;
};

export type AdminObservabilityAggregateViewModel = {
  title: string;
  sourceGapLabel?: string;
  items: AdminObservabilityAggregateItemViewModel[];
};

function formatFieldLabel(
  field: AdminObservabilityAggregateField,
  locale: AdminDashboardLocale,
): string {
  switch (field) {
    case "businessFlowCompletionRate":
      return pickAdminDashboardLocaleText(locale, { en: "Completion rate", ko: "완료율" });
    case "failureReasonBucketSummary":
      return pickAdminDashboardLocaleText(locale, { en: "Failure reason", ko: "실패 원인" });
    case "releaseErrorSpikeSummary":
      return pickAdminDashboardLocaleText(locale, { en: "Release spike", ko: "릴리스 급증" });
    case "jobRetryPressure":
      return pickAdminDashboardLocaleText(locale, { en: "Retry pressure", ko: "재시도 압력" });
    case "oldestQueuedAgeBucket":
      return pickAdminDashboardLocaleText(locale, { en: "Oldest queued age", ko: "최장 대기 시간" });
    case "terminalFailureCodeDistribution":
      return pickAdminDashboardLocaleText(locale, { en: "Terminal failures", ko: "최종 실패 분포" });
    case "providerFailureDistribution":
      return pickAdminDashboardLocaleText(locale, { en: "Provider failures", ko: "제공자 실패 분포" });
    default:
      return field;
  }
}

function formatFieldValue(
  value: string,
  locale: AdminDashboardLocale,
): string {
  const normalizedValue = value.trim().toLowerCase();

  if (locale === "ko") {
    switch (normalizedValue) {
      case "stable":
        return "안정";
      case "low":
        return "낮음";
      case "contained":
        return "통제됨";
      case "under 15 min":
      case "under_15_min":
        return "15분 미만";
      case "unknown":
        return "알 수 없음";
      default:
        return value;
    }
  }

  switch (normalizedValue) {
    case "stable":
      return "Stable";
    case "low":
      return "Low";
    case "contained":
      return "Contained";
    case "under 15 min":
    case "under_15_min":
      return "Under 15 min";
    case "unknown":
      return "Unknown";
    default:
      return value;
  }
}

export function buildAdminObservabilityAggregateViewModel(
  summary: AdminObservabilityAggregateSummary | undefined,
  locale: AdminDashboardLocale,
  fields: AdminObservabilityAggregateField[],
): AdminObservabilityAggregateViewModel | undefined {
  if (!summary) {
    return undefined;
  }

  const items = fields.flatMap((field) => {
    const value = summary[field];
    if (!value) {
      return [];
    }

    return [{
      id: field,
      label: formatFieldLabel(field, locale),
      value: formatFieldValue(value, locale),
    }];
  });

  return {
    title: pickAdminDashboardLocaleText(locale, { en: "Observability", ko: "관측 집계" }),
    sourceGapLabel: items.length === 0
      ? pickAdminDashboardLocaleText(locale, {
          en: "Aggregate source not populated yet. Verify the upstream observability summary before treating this state as healthy.",
          ko: "집계 source가 아직 채워지지 않았습니다. 이 상태를 정상으로 보기 전에 upstream 관측 요약 source를 먼저 확인하세요.",
        })
      : undefined,
    items,
  };
}
